import { formatPercentage } from '@helpers';
import clsx from 'clsx';
import React from 'react';

import { LABEL_WIDTH, margin } from '../constants';
import styles from '../HorizontalBarChart.module.scss';

export interface AxisProps extends React.SVGProps<SVGGElement> {
  headerX: number;
  headerLabel: string;
  xScale: d3.ScaleLinear<number, number, never>;
  tickValues: number[];
  height: number;
  useOriginalValue: boolean;
  prefix: string;
}

export const Axis = ({
  headerX,
  headerLabel,
  xScale,
  tickValues,
  height,
  useOriginalValue,
  prefix,
  ...props
}: AxisProps) => {
  const isEven = (i: number) => i % 2 === 0;
  const zeroPoint = tickValues.findIndex(t => t === 0);
  const strokeWidth = (tick: number) => (tick === 0 ? 2 : 1);

  const getLineStroke = (i: number) => {
    if (zeroPoint > -1 && isEven(zeroPoint)) {
      return isEven(i) ? undefined : '2,2';
    } else {
      return isEven(i) ? '2,2' : undefined;
    }
  };

  return (
    <g data-testid="axis-root" {...props}>
      {headerLabel && (
        <text
          className={clsx(styles.axisText, styles.axisTextHeader)}
          data-testid="y-axis-right-header"
          x={headerX}
        >
          {headerLabel}
        </text>
      )}

      {tickValues.map((tick, i) => {
        const baseAxisText = formatPercentage(tick, { useOriginalValue });
        const axisText =
          tick > 0 && !!prefix ? `${prefix}${baseAxisText}` : baseAxisText;
        return (
          <g
            key={i}
            transform={`translate(${xScale(tick) + LABEL_WIDTH}, 0)`}
            data-testid="x-axis-item"
          >
            <line
              className={styles.axisLine}
              data-testid="x-axis-vertical-line"
              y1={margin.top}
              y2={height}
              strokeWidth={strokeWidth(tick)}
              strokeDasharray={getLineStroke(i)}
            />
            <text
              className={styles.axisText}
              y={0}
              data-testid="x-axis-text-item"
            >
              {axisText}
            </text>
          </g>
        );
      })}
    </g>
  );
};
